import React from "react";
// core components
import ReactTableV2 from "components/ReactTable/ReactTableV2";

// material
import { TextField, Box, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Divider, Fade, Chip } from "@material-ui/core";
import { KeyboardArrowDown as KeyboardArrowDownIcon } from "components/MaterialSymbol/MaterialIcons";
import { KeyboardArrowRight as KeyboardArrowRightIcon } from "components/MaterialSymbol/MaterialIcons";
import { Email as EmailIcon } from "components/MaterialSymbol/MaterialIcons";
import { safeParse } from "helpers";
import Skeleton from "@material-ui/lab/Skeleton";
import useOrderEmailLog from "hooks/useOrderEmailLog";

const isMaybeJson = (val) => {
    if (typeof val !== "string") return false;
    const trimmed = val.trim();
    const startsWithBrace = trimmed.substring(0, 1) === "{" || trimmed.substring(0, 1) === "[";
    const endsWithBrace = trimmed.substring(trimmed.length - 1) === "}" || trimmed.substring(trimmed.length - 1) === "]";
    return startsWithBrace && endsWithBrace;
};

// Helper that wraps the stored HTML in a minimal base document for the iframe
const wrapWithBaseHtml = (content) => `
    <!DOCTYPE html>
    <html lang="en">
    <head>
        <meta charset="utf-8">
        <style>
            body { 
                margin: 0; 
                padding: 40px; 
                background-color: #fff;
                font-family: Helvetica, Arial, sans-serif;
                font-size: 14px;
                color: #333;
                line-height: 1.4;
            }
            /* Ensure tables and elements in the stored HTML behave natively */
            table { border-collapse: collapse; }
            img { max-width: 100%; height: auto; }
        </style>
    </head>
    <body>
        ${content}
    </body>
    </html>
`;

const NestedJsonViewer = ({ data, label = null, initialOpen = false }) => {
    const [isOpen, setIsOpen] = React.useState(initialOpen);
    const isObject = data !== null && typeof data === "object";

    if (!isObject) {
        return (
            <Box display="flex" ml={label ? 2 : 0}>
                {label && (
                    <Typography variant="caption" color="textSecondary" style={{ marginRight: 8, fontWeight: "bold" }}>
                        {label}:
                    </Typography>
                )}
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {String(data)}
                </Typography>
            </Box>
        );
    }

    const entries = Object.entries(data);

    return (
        <Box ml={label ? 2 : 0}>
            <Box display="flex" alignItems="center" style={{ cursor: "pointer" }} onClick={() => setIsOpen(!isOpen)}>
                <IconButton size="small" style={{ padding: 0, marginRight: 4 }}>
                    {isOpen ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                </IconButton>
                {label && (
                    <Typography variant="caption" color="textSecondary" style={{ marginRight: 8, fontWeight: "bold" }}>
                        {label}:
                    </Typography>
                )}
                <Typography variant="caption" color="primary">
                    {Array.isArray(data) ? `Array[${entries.length}]` : `Object {${entries.length}}`}
                </Typography>
            </Box>
            {isOpen && (
                <Box borderLeft="1px solid #e0e0e0" ml={1} pl={1}>
                    {entries.map(([key, value]) => (
                        <NestedJsonViewer key={key} label={key} data={value} initialOpen={initialOpen} />
                    ))}
                </Box>
            )}
        </Box>
    );
};

const EmailLogDialog = ({ open, onClose, orderId }) => {
    const { emailLog, loading } = useOrderEmailLog(open ? { orderId, type: "customer_confirmation" } : null);

    return (
        <Dialog 
            open={open} 
            onClose={onClose} 
            maxWidth="lg" 
            fullWidth
            TransitionComponent={Fade}
            transitionDuration={400}
        >
            <DialogTitle style={{ backgroundColor: "#fbfbfb", borderBottom: "1px solid #eee", padding: "12px 24px" }}>Email Audit Trait</DialogTitle>
            <DialogContent dividers style={{ backgroundColor: "#f4f4f4", padding: 0 }}>
                {loading ? (
                    <Box p={4} style={{ backgroundColor: "#fff", height: "70vh" }}>
                        <Skeleton variant="text" width="80%" height={40} />
                        <Box mt={2} mb={2}>
                            <Skeleton variant="text" width="40%" />
                            <Skeleton variant="text" width="60%" />
                            <Skeleton variant="text" width="30%" />
                        </Box>
                        <Divider />
                        <Box mt={4}>
                            <Skeleton variant="rect" width="100%" height={300} />
                        </Box>
                    </Box>
                ) : emailLog ? (
                    <Box 
                        style={{ 
                            backgroundColor: "#fff", 
                            maxWidth: 1000, 
                            margin: "0 auto", 
                            minHeight: "75vh",
                            boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        {/* Header Metadata */}
                        <Box p={4} pb={0}>
                            <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start">
                                <Box flex={1}>
                                    <Typography variant="h6" style={{ fontWeight: 600, color: "#111", fontSize: "1.4rem", letterSpacing: "-0.01em", marginBottom: 16 }}>
                                        {emailLog.subject}
                                    </Typography>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        <Typography variant="body2" style={{ color: "#666", minWidth: 45, fontWeight: 500, fontSize: "0.85rem" }}>
                                            From:
                                        </Typography>
                                        <Chip 
                                            label="noreply@ordernotification.net" 
                                            size="small" 
                                            variant="outlined" 
                                            style={{ backgroundColor: "#f8f9fa", borderColor: "#e0e0e0", color: "#555", fontSize: "0.8rem", height: 24 }} 
                                        />
                                    </Box>
                                    <Box display="flex" alignItems="center">
                                        <Typography variant="body2" style={{ color: "#666", minWidth: 45, fontWeight: 500, fontSize: "0.85rem" }}>
                                            To:
                                        </Typography>
                                        <Chip 
                                            label={emailLog.recipient} 
                                            size="small" 
                                            color="primary"
                                            variant="outlined"
                                            style={{ backgroundColor: "#eff6ff", color: "#1e40af", borderColor: "#bfdbfe", fontSize: "0.8rem", height: 24, fontWeight: 500 }} 
                                        />
                                    </Box>
                                </Box>
                                <Box ml={4} pt={0.5}>
                                    <Typography variant="body2" color="textSecondary" style={{ fontSize: "0.85rem", whiteSpace: "nowrap", color: "#888" }}>
                                        {emailLog.createdAt ? new Date(emailLog.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "N/A"}
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider style={{ margin: "16px 0 0 0", backgroundColor: "#f0f0f0" }} />
                        </Box>
                        
                        {/* Email Content Isolation (iFrame) */}
                        <Box flex={1}>
                            <iframe
                                title="email-audit-preview"
                                srcDoc={wrapWithBaseHtml(emailLog.contentHtml)}
                                style={{
                                    width: "100%",
                                    height: "60vh",
                                    border: "none",
                                    display: "block"
                                }}
                            />
                        </Box>
                    </Box>
                ) : (
                    <Box 
                        display="flex" 
                        flexDirection="column" 
                        alignItems="center" 
                        justifyContent="center" 
                        style={{ height: "65vh", p: 4, backgroundColor: "#fafafa" }}
                    >
                        <Box 
                            display="flex" 
                            flexDirection="column" 
                            alignItems="center" 
                            style={{ 
                                animation: "fadeIn 0.5s ease-out",
                                maxWidth: 500,
                                textAlign: "center"
                            }}
                        >
                            <Box 
                                mb={3} 
                                style={{ 
                                    color: "#cbd5e1",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    backgroundColor: "#fff",
                                    width: 140,
                                    height: 140,
                                    borderRadius: "50%",
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
                                }}
                            >
                                <EmailIcon style={{ fontSize: "5rem", color: "#94a3b8" }} />
                            </Box>
                            
                            <Typography variant="h6" style={{ fontWeight: 600, color: "#1e293b", marginBottom: 12 }}>
                                No email log found
                            </Typography>
                            
                            <Typography variant="body2" style={{ color: "#64748b", lineHeight: 1.6, marginBottom: 24, fontSize: "0.95rem" }}>
                                This functionality was implemented on April 2, 2026. 
                                Orders created before that date do not have stored email logs or audit traits.
                            </Typography>

                            <Box 
                                p={1} 
                                px={3} 
                                style={{ 
                                    backgroundColor: "#f1f5f9", 
                                    borderRadius: 30, 
                                    display: "inline-block" 
                                }}
                            >
                                <Typography variant="caption" style={{ textTransform: "uppercase", letterSpacing: 1.5, color: "#475569", fontWeight: 600 }}>
                                    Audit History Not Recorded
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="primary" variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default function OrderLog({ formik }) {
    const columns = [
        {
            Header: "Created at",
            accessor: "createdAt",
            width: 180,
            Cell: ({ value }) => (value ? new Date(value).toLocaleString() : ""),
        },
        {
            Header: "User",
            accessor: "user",
            width: 150,
        },
        {
            Header: "Action",
            accessor: "action",
            width: 180,
            Cell: ({ row: { original } }) => {
                let label = original.action.replace(/_/g, " ");
                let color = "inherit";

                if (original.action === "STATUS_CHANGE") {
                    label = "STATUS CHANGE";
                } else if (original.action === "GLOBAL_SHIPMENT_ADDED") {
                    label = "GLOBAL TRACKING UPDATE";
                } else if (original.action === "ITEM_SHIPMENT_ADDED") {
                    label = "PER-ITEM TRACKING UPDATE";
                }

                return (
                    <Typography variant="body2" style={{ fontWeight: "bold", color }}>
                        {label}
                    </Typography>
                );
            },
        },
        {
            Header: "Memo",
            width: 500,
            Cell: ({ row: { original } }) => {
                const memoData = isMaybeJson(original.memo) ? safeParse(original.memo, original.memo) : original.memo;
                return (
                    <Box display="flex" alignItems="center" gridGap={4}>
                        <Box
                            maxWidth={500}
                            maxHeight={300}
                            overflow="auto"
                            p={1}
                            style={{ backgroundColor: "#fcfcfc", borderRadius: 4, border: "1px solid #eee" }}
                        >
                            <NestedJsonViewer data={memoData} initialOpen={true} />
                        </Box>
                        {original.action === "NEW_STORE_ORDER" && (
                            <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                startIcon={<EmailIcon style={{ fontSize: "1rem" }} />}
                                onClick={() => openEmailDialog(formik.values.id)}
                                style={{
                                    textTransform: "none",
                                    fontSize: "0.75rem",
                                    whiteSpace: "nowrap",
                                    marginLeft: 8,
                                    borderColor: "rgba(63, 81, 181, 0.5)", // Subtle primary color border
                                }}
                            >
                                View Email
                            </Button>
                        )}
                    </Box>
                );
            },
        },
    ];

    const [emailDialog, setEmailDialog] = React.useState({ open: false, orderId: null });

    const openEmailDialog = (orderId) => {
        setEmailDialog({ open: true, orderId });
    };

    const closeEmailDialog = () => {
        setEmailDialog({ open: false, orderId: null });
    };

    return (
        <>
            <ReactTableV2 data={formik.values.log || []} columns={columns} rowKey="id" />
            <EmailLogDialog
                open={emailDialog.open}
                onClose={closeEmailDialog}
                orderId={emailDialog.orderId}
            />
        </>
    );
}
